/**
 * Interview Feedback Types
 * Types for the assessment feedback system after realtime interviews
 */

// ============================================================================
// Base Types
// ============================================================================

/**
 * Assessment submission payload
 */
export interface AssessmentPayload {
  conversation_id: string;
  question_id: string;
  pronunciation_assessment: number; // 0-10
  transcript: TranscriptMessage[];
  usage: UsageMetrics;
  meeting_id: string; // UUID v4 tracking this specific meeting training session
  meeting_name: string; // Name of the meeting
  meeting_category: string; // Category of the meeting
}

/**
 * Transcript message in conversation
 */
export interface TranscriptMessage {
  role: "assistant" | "user";
  content: string;
  sequence: number;
}

/**
 * Token usage metrics from Azure OpenAI
 */
export interface UsageMetrics {
  total_tokens?: number;
  input_tokens?: number;
  output_tokens?: number;
  input_token_details?: {
    text_tokens: number;
    audio_tokens: number;
    cached_tokens: number;
    cached_tokens_details?: {
      text_tokens: number;
      audio_tokens: number;
    };
  };
  output_token_details?: {
    text_tokens: number;
    audio_tokens: number;
  };
}

// ============================================================================
// Assessment Response Types
// ============================================================================

/**
 * Response from assessment submission
 */
export interface AssessmentSubmissionResponse {
  assessment_id: string;
  status: AssessmentStatus;
  created_at: string;
}

/**
 * Assessment processing status
 */
export type AssessmentStatus = "pending" | "processing" | "completed" | "error";

/**
 * Poll status response
 */
export interface PollStatusResponse {
  assessment_id: string;
  status: AssessmentStatus;
  progress?: number;
  estimated_completion_time?: string;
  completed_at?: string;
}

// ============================================================================
// Feedback Block Types
// ============================================================================

/**
 * Base interface for all feedback blocks
 */
interface BaseFeedbackBlock {
  type: string;
  title: string;
  helper?: string;
  position: number;
}

/**
 * Gauge block - circular progress indicator
 */
export interface GaugeBlock extends BaseFeedbackBlock {
  type: "gauge";
  value: number;
  maxValue: number;
}

/**
 * Score grid item
 */
export interface ScoreGridItem {
  title: string;
  helper?: string;
  score: number;
  maxScore: number;
  bar_color?: string;
  bar_background_color?: string;
}

/**
 * Score grid block - multiple scores in a grid
 */
export interface ScoreGridBlock extends BaseFeedbackBlock {
  type: "score_grid";
  items: ScoreGridItem[];
}

/**
 * Text block item
 */
export interface TextBlockItem {
  text: string;
}

/**
 * Text block - list of text items
 */
export interface TextBlock extends BaseFeedbackBlock {
  type: "text_block";
  items: TextBlockItem[];
}

/**
 * Transcript summary with mistakes and corrections
 */
export interface TranscriptSummary {
  mistakes: {
    color: string;
    sentences: string[];
  };
  corrections: {
    color: string;
    sentences: string[];
  };
}

/**
 * Transcript block - full conversation with analysis
 */
export interface TranscriptBlock extends BaseFeedbackBlock {
  type: "transcript";
  transcript: TranscriptMessage[];
  summary: TranscriptSummary;
}

/**
 * Union type for all feedback blocks
 */
export type FeedbackBlock =
  | GaugeBlock
  | ScoreGridBlock
  | TextBlock
  | TranscriptBlock;

// ============================================================================
// Full Assessment Response
// ============================================================================

/**
 * Interview practice feedback structure
 */
export interface InterviewPracticeFeedback {
  blocks: FeedbackBlock[];
}

/**
 * Complete assessment with feedback
 */
export interface AssessmentFeedbackResponse {
  id: string;
  assessment_id: string;
  realtime_id: string;
  question_id: string;
  created_at: string;
  completed_at: string;
  meeting_name?: string; // Name of the meeting
  meeting_category?: string; // Category of the meeting
  interviewPracticeFeedback: InterviewPracticeFeedback;
}

// ============================================================================
// Helper Type Guards
// ============================================================================

/**
 * Type guard for GaugeBlock
 */
export function isGaugeBlock(block: FeedbackBlock): block is GaugeBlock {
  return block.type === "gauge";
}

/**
 * Type guard for ScoreGridBlock
 */
export function isScoreGridBlock(
  block: FeedbackBlock
): block is ScoreGridBlock {
  return block.type === "score_grid";
}

/**
 * Type guard for TextBlock
 */
export function isTextBlock(block: FeedbackBlock): block is TextBlock {
  return block.type === "text_block";
}

/**
 * Type guard for TranscriptBlock
 */
export function isTranscriptBlock(
  block: FeedbackBlock
): block is TranscriptBlock {
  return block.type === "transcript";
}

// ============================================================================
// UI State Types
// ============================================================================

/**
 * Progress messages for polling
 */
export const PROGRESS_MESSAGES = [
  "Analyzing your responses...",
  "Evaluating communication clarity...",
  "Reviewing answer structure...",
  "Calculating pronunciation scores...",
  "Generating personalized feedback...",
  "Finalizing your assessment report...",
] as const;

export type ProgressMessage = (typeof PROGRESS_MESSAGES)[number];

/**
 * Polling configuration
 */
export const POLLING_CONFIG = {
  INTERVAL_MS: 2000, // 2 seconds
  MAX_ATTEMPTS: 60, // 60 attempts = 2 minutes
  TIMEOUT_MS: 120000, // 2 minutes total
  MESSAGE_UPDATE_INTERVAL: 3, // Update message every 3 attempts (6s)
} as const;
